import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { generateDiagnosticPDF } from '../../../utils/diagnosticPdfGenerator';

const DiagnosticPDFExport = ({ overallScore, overallMaturity, domainScores, insights, onClose }) => {
  const [step, setStep] = useState('form'); // form | generating | done | error
  const [formData, setFormData] = useState({ fullName: '', company: '', email: '' });
  const [errors, setErrors] = useState({});
  const [errorMsg, setErrorMsg] = useState('');

  const validate = () => {
    const errs = {};
    if (!formData?.fullName?.trim()) errs.fullName = 'Full name is required';
    if (!formData?.company?.trim()) errs.company = 'Company name is required';
    if (!formData?.email?.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) errs.email = 'Please enter a valid email';
    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleDownload = async () => {
    if (!validate()) return;
    setStep('generating');
    setErrorMsg('');

    try {
      // Save lead to Supabase
      const { error: dbError } = await supabase?.from('diagnostic_leads')?.insert({
          full_name: formData?.fullName?.trim(),
          company: formData?.company?.trim(),
          email: formData?.email?.trim()?.toLowerCase(),
          overall_score: parseFloat(overallScore?.toFixed(1)),
          maturity_level: overallMaturity?.label || '',
          domain_scores: domainScores?.map(({ domain, avg }) => ({
            id: domain?.id,
            title: domain?.title,
            score: parseFloat(avg?.toFixed(1)),
          })) || [],
          insights: {
            criticalRisks: insights?.criticalRisks || [],
            quickWins: insights?.quickWins || [],
            roadmap: insights?.roadmap || [],
          },
        });

      if (dbError) {
        console.warn('Lead save warning:', dbError?.message);
        // Non-blocking — still generate PDF even if DB save fails
      }

      // Generate PDF
      generateDiagnosticPDF({
        leadInfo: {
          fullName: formData?.fullName?.trim(),
          company: formData?.company?.trim(),
          email: formData?.email?.trim(),
        },
        overallScore,
        overallMaturity,
        domainScores,
        insights,
      });

      setStep('done');
    } catch (err) {
      console.error('PDF generation error:', err);
      setErrorMsg('Something went wrong generating your report. Please try again.');
      setStep('error');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#002B45] to-[#004070] px-6 py-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="p-1.5 bg-white/10 rounded-lg">
                  <Icon name="FileDown" size={18} className="text-white" />
                </div>
                <span className="text-white/70 text-xs font-semibold uppercase tracking-wider">Northstar Analytics</span>
              </div>
              <h2 className="text-white text-xl font-bold">Download Your Report</h2>
              <p className="text-white/70 text-sm mt-1">Get your branded Data Governance Diagnostic PDF</p>
            </div>
            <button
              onClick={onClose}
              className="text-white/50 hover:text-white transition-colors mt-1"
              aria-label="Close"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        {/* Teal accent strip */}
        <div className="h-1 bg-[#1FB5A5]" />

        <div className="px-6 py-6">
          {/* Score preview */}
          <div className="flex items-center gap-4 bg-gray-50 rounded-xl p-4 mb-6 border border-border">
            <div className="text-center">
              <div className="text-3xl font-bold text-[#002B45]">{overallScore?.toFixed(1)}</div>
              <div className="text-xs text-gray-400 font-medium">/ 5.0</div>
            </div>
            <div className="flex-1 border-l border-border pl-4">
              <div className="font-bold text-[#002B45]">{overallMaturity?.label}</div>
              <div className="text-xs text-gray-500 mt-0.5">{overallMaturity?.description}</div>
            </div>
            <div className="flex-shrink-0">
              <Icon name="FileBarChart" size={28} className="text-[#1FB5A5]" />
            </div>
          </div>

          {step === 'form' && (
            <>
              <p className="text-sm text-gray-500 mb-5">
                Enter your details to receive your personalised Northstar Diagnostic Report — includes your maturity score, domain breakdown, critical risks, quick wins, and 90-day roadmap.
              </p>

              <div className="space-y-4">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Icon name="User" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={formData?.fullName}
                      onChange={e => handleChange('fullName', e?.target?.value)}
                      placeholder="e.g. Sarah Johnson"
                      className={`w-full pl-9 pr-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1FB5A5]/30 transition-colors ${errors?.fullName ? 'border-red-400 bg-red-50' : 'border-border bg-white'}`}
                    />
                  </div>
                  {errors?.fullName && <p className="text-xs text-red-500 mt-1">{errors?.fullName}</p>}
                </div>

                {/* Company */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Company / Organisation <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Icon name="Building2" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={formData?.company}
                      onChange={e => handleChange('company', e?.target?.value)}
                      placeholder="e.g. Acme Corporation"
                      className={`w-full pl-9 pr-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1FB5A5]/30 transition-colors ${errors?.company ? 'border-red-400 bg-red-50' : 'border-border bg-white'}`}
                    />
                  </div>
                  {errors?.company && <p className="text-xs text-red-500 mt-1">{errors?.company}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Work Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Icon name="Mail" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      value={formData?.email}
                      onChange={e => handleChange('email', e?.target?.value)}
                      placeholder="e.g. sarah@company.com"
                      className={`w-full pl-9 pr-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1FB5A5]/30 transition-colors ${errors?.email ? 'border-red-400 bg-red-50' : 'border-border bg-white'}`}
                    />
                  </div>
                  {errors?.email && <p className="text-xs text-red-500 mt-1">{errors?.email}</p>}
                </div>
              </div>

              <p className="text-xs text-gray-400 mt-4 flex items-start gap-1.5">
                <Icon name="Lock" size={12} className="flex-shrink-0 mt-0.5" />
                Your details are kept confidential and used only to personalise your report. We may follow up with relevant insights from Northstar Analytics.
              </p>

              <Button
                onClick={handleDownload}
                className="w-full mt-5 bg-[#1FB5A5] hover:bg-[#17a396] text-white py-3"
              >
                <Icon name="Download" size={16} className="mr-2" />
                Generate & Download Report
              </Button>
            </>
          )}

          {step === 'generating' && (
            <div className="text-center py-8">
              <div className="w-14 h-14 rounded-full bg-[#1FB5A5]/10 flex items-center justify-center mx-auto mb-4 animate-pulse">
                <Icon name="FileBarChart" size={28} className="text-[#1FB5A5]" />
              </div>
              <h3 className="font-bold text-[#002B45] text-lg mb-2">Building Your Report…</h3>
              <p className="text-sm text-gray-500">Compiling your maturity scores, risks, quick wins, and 90-day roadmap into a branded PDF.</p>
            </div>
          )}

          {step === 'done' && (
            <div className="text-center py-6">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle2" size={28} className="text-green-600" />
              </div>
              <h3 className="font-bold text-[#002B45] text-lg mb-2">Report Downloaded!</h3>
              <p className="text-sm text-gray-500 mb-6">
                Your Northstar Data Governance Diagnostic Report has been saved to your device. Share it with your team or use it to kick off your governance transformation.
              </p>
              <div className="bg-[#002B45]/5 rounded-xl p-4 mb-5 text-left">
                <p className="text-xs font-semibold text-[#002B45] uppercase tracking-wider mb-2">What's in your report</p>
                {['Overall maturity score & level', 'Domain-level breakdown (6 areas)', 'Top 3 critical risks', 'Top 3 quick wins', '90-day improvement roadmap']?.map(item => (
                  <div key={item} className="flex items-center gap-2 text-sm text-gray-600 py-0.5">
                    <Icon name="Check" size={13} className="text-[#1FB5A5] flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
              <Button onClick={onClose} className="w-full bg-[#002B45] hover:bg-[#003560] text-white">
                Close
              </Button>
            </div>
          )}

          {step === 'error' && (
            <div className="text-center py-6">
              <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertCircle" size={28} className="text-red-600" />
              </div>
              <h3 className="font-bold text-[#002B45] text-lg mb-2">Something Went Wrong</h3>
              <p className="text-sm text-gray-500 mb-5">{errorMsg}</p>
              <Button onClick={() => setStep('form')} className="w-full">
                Try Again
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiagnosticPDFExport;
